import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import {
  translateText,
  SUPPORTED_LANGUAGES,
  LANGUAGE_FLAGS,
  LANGUAGE_NATIVE_NAMES,
  type LanguageCode,
} from "@/features/chat/services/translationService";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { returnHomeService } from "@/features/return-home/services/returnHomeService";
import {
  getPresignedUrl,
  uploadToS3Direct,
  confirmAttachmentInDB,
  deleteAttachmentFromAll,
} from "@/shared/services/uploadService";
import { announcementService } from "@/features/announcements/services/feedService";
import { getMimeType } from "@/shared/utils/fileHelpers";
import { useFormatDate } from "@/shared/hooks/useFormatDate";
import { formatDisplayName } from "@/shared/utils/formatters";
import { Spinner } from "@/shared/components/ui/Spinner";
import { Input } from "@/shared/components/ui/Input";
import { Button } from "@/shared/components/ui/Button";
import { Badge } from "@/shared/components/ui/Badge";
import { Toast } from "@/shared/components/feedback/Toast";
import { AttachmentDropZone } from "@/features/announcements/components/AttachmentDropZone";
import { AttachmentList } from "@/features/announcements/components/AttachmentList";
import { CommentSection } from "@/features/announcements/components/CommentSection";
import { TripSummaryCard } from "@/features/return-home/components/TripSummaryCard";
import {
  COUNTRIES,
  AIRPORTS,
  getAirportsByCountry,
  type Airport,
} from "@/features/return-home/data/airports";
import type {
  ReturnHomeRecord,
  ReturnHomeFormData,
  ReturnHomeStatus,
} from "@/features/return-home/types/returnHome";
import type {
  AnnouncementAttachment,
  AttachmentUploadItem,
} from "@/features/announcements/types/feed";

const OFFICER_TYPES = ["officer", "admin"];
const ACCEPTED_MIME_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/gif"];
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const emptyForm: ReturnHomeFormData = {
  flight_date: "",
  return_date: "",
  route_origin: "",
  route_destination: "",
  ticket_type: "",
  return_type: -1,
  tio_jo: "",
  details: "",
  status: "Draft",
  resign_date: null,
  leave_days: null,
  mode_of_payment: null,
  payment_amount: null,
  currency: "JPY",
  payment_settled: false,
};

type ReturnHomeCategory =
  | "none"
  | "paid_leave"
  | "resignation"
  | "resignation_lumpsum"
  | "no_paid_leave"
  | "finished_contract";

const computeStatus = (flightDate: string, returnDate: string): ReturnHomeStatus => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const flight = flightDate ? new Date(flightDate) : null;
  const returnDt = returnDate ? new Date(returnDate) : null;
  if (!flight) return "";
  flight.setHours(0, 0, 0, 0);
  if (returnDt) returnDt.setHours(0, 0, 0, 0);
  if (today < flight) return "Upcoming";
  if (returnDt && today > returnDt) return "Returned";
  if (today >= flight) return "Out of Country";
  return "";
};

const STATUS_I18N_MAP: Record<string, string> = {
  Upcoming: "return_home.status.upcoming",
  "Out of Country": "return_home.status.out_of_country",
  Returned: "return_home.status.returned",
};

const statusVariant: Record<string, "default" | "success" | "warning" | "danger" | "info"> = {
  Upcoming: "info",
  "Out of Country": "warning",
  Returned: "success",
};

const APPROVAL_VARIANT: Record<string, "default" | "success" | "warning" | "danger" | "info"> = {
  Draft: "default",
  Pending: "warning",
  Approved: "success",
  Rejected: "danger",
};

const CATEGORY_LABEL_KEY: Record<ReturnHomeCategory, string | null> = {
  none: null,
  paid_leave: "return_home.form.category_paid_leave",
  no_paid_leave: "return_home.form.category_no_paid_leave",
  resignation: "return_home.form.category_resignation",
  resignation_lumpsum: "return_home.form.category_resignation_two",
  finished_contract: "return_home.form.category_finished_contract",
};

type TabId = "info" | "comments" | "files";

// Shown in place of a disabled input when the record is locked for editing.
const ReadOnlyField: React.FC<{ id?: string; label: string; value: React.ReactNode; className?: string }> = ({
  id,
  label,
  value,
  className = "",
}) => (
  <div id={id} className={className}>
    <p className="text-xs text-gray-500 font-medium mb-0.5">{label}</p>
    <div className="text-sm text-gray-800 whitespace-pre-wrap">{value || "—"}</div>
  </div>
);

interface ReturnHomeDetailPanelProps {
  recordId: string | null;
  initialCommentCount?: number;
  isOpen: boolean;
  onClose: () => void;
  onRefresh: () => void;
}

export const ReturnHomeDetailPanel: React.FC<ReturnHomeDetailPanelProps> = ({
  recordId,
  initialCommentCount = 0,
  isOpen,
  onClose,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const { fmtDate } = useFormatDate();
  const { userData } = useAuth();
  const { canWrite } = usePermissions();
  const isOfficer = OFFICER_TYPES.includes(userData?.userType?.toLowerCase() || "");
  const canApprove = canWrite("flight");

  const [activeTab, setActiveTab] = useState<TabId>("info");
  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState<ReturnHomeRecord | null>(null);
  const [form, setForm] = useState<ReturnHomeFormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});

  // Category
  const [category, setCategory] = useState<ReturnHomeCategory>("none");

  // Smart route selection
  const [originCountry, setOriginCountry] = useState("");
  const [destCountry, setDestCountry] = useState("");
  const [originAirports, setOriginAirports] = useState<Airport[]>([]);
  const [destAirports, setDestAirports] = useState<Airport[]>([]);

  // Approval
  const [approverRemarks, setApproverRemarks] = useState("");
  const [approving, setApproving] = useState(false);

  // Translation
  const [translatedDetails, setTranslatedDetails] = useState<string | null>(null);
  const [translatedApproverComment, setTranslatedApproverComment] = useState<string | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [activeLang, setActiveLang] = useState<LanguageCode | null>(null);
  const [langPickerOpen, setLangPickerOpen] = useState(false);
  const langPickerRef = useRef<HTMLDivElement>(null);

  // Attachments
  const [attachments, setAttachments] = useState<AnnouncementAttachment[]>([]);
  const [uploads, setUploads] = useState<AttachmentUploadItem[]>([]);

  // Comment count
  const [commentCount, setCommentCount] = useState(0);

  const showToast = useCallback((message: string, type: "success" | "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const populateForm = useCallback((rec: ReturnHomeRecord) => {
    const derivedCategory: ReturnHomeCategory =
      rec.return_type === 0 ? "paid_leave" :
      rec.return_type === 1 ? "resignation" :
      rec.return_type === 2 ? "resignation_lumpsum" :
      rec.return_type === 3 ? "no_paid_leave" :
      rec.return_type === 4 ? "finished_contract" : "none";
    setCategory(derivedCategory);

    setForm({
      id: rec.id,
      user_id: rec.user_id,
      flight_date: rec.flight_date ? rec.flight_date.slice(0, 10) : "",
      return_date: rec.return_date ? rec.return_date.slice(0, 10) : "",
      route_origin: rec.route_origin?.trim() || "",
      route_destination: rec.route_destination?.trim() || "",
      ticket_type: rec.ticket_type || "",
      return_type: rec.return_type ?? -1,
      tio_jo: rec.tio_jo || "",
      details: rec.details || "",
      status: rec.status || "Draft",
      resign_date: rec.resign_date ? rec.resign_date.slice(0, 10) : null,
      leave_days: rec.leave_days ?? null,
      mode_of_payment: rec.mode_of_payment ?? null,
      payment_amount: rec.payment_amount != null ? Number(rec.payment_amount) : null,
      currency: rec.currency || "JPY",
      payment_settled: rec.payment_settled ?? false,
    });
    setApproverRemarks("");

    // Reverse-lookup countries from airport codes
    if (rec.route_origin) {
      const code = rec.route_origin.trim();
      const airport = AIRPORTS.find((a) => a.code === code);
      if (airport) {
        setOriginCountry(airport.country);
        setOriginAirports(getAirportsByCountry(airport.country));
      } else {
        setOriginCountry("");
        setOriginAirports([]);
      }
    } else {
      setOriginCountry("");
      setOriginAirports([]);
    }
    if (rec.route_destination) {
      const code = rec.route_destination.trim();
      const airport = AIRPORTS.find((a) => a.code === code);
      if (airport) {
        setDestCountry(airport.country);
        setDestAirports(getAirportsByCountry(airport.country));
      } else {
        setDestCountry("");
        setDestAirports([]);
      }
    } else {
      setDestCountry("");
      setDestAirports([]);
    }

    if (rec.attachments && Array.isArray(rec.attachments)) {
      setAttachments(rec.attachments.map((a: any) => ({
        ...a,
        display_name: a.display_name || a.name || "",
        file_type: a.file_type || a.type || "",
      })));
    } else {
      setAttachments([]);
    }
  }, []);

  // Load record when panel opens
  useEffect(() => {
    if (!isOpen || !recordId) return;
    setActiveTab("info");
    setError("");
    setFieldErrors({});
    setTranslatedDetails(null);
    setTranslatedApproverComment(null);
    setActiveLang(null);
    setUploads([]);
    setCommentCount(0);
    setLoading(true);

    returnHomeService.getById(recordId)
      .then((data) => {
        if (data) {
          setRecord(data);
          populateForm(data);
          setCommentCount(data.comment_count ?? 0);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [isOpen, recordId, populateForm]);

  // Update airports when country changes
  useEffect(() => {
    setOriginAirports(originCountry ? getAirportsByCountry(originCountry) : []);
    if (!originCountry) setForm((prev) => ({ ...prev, route_origin: "" }));
  }, [originCountry]);

  useEffect(() => {
    setDestAirports(destCountry ? getAirportsByCountry(destCountry) : []);
    if (!destCountry) setForm((prev) => ({ ...prev, route_destination: "" }));
  }, [destCountry]);

  // Close language picker on outside click
  useEffect(() => {
    if (!langPickerOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (langPickerRef.current && !langPickerRef.current.contains(e.target as Node))
        setLangPickerOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [langPickerOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (fieldErrors[name]) setFieldErrors((prev) => ({ ...prev, [name]: false }));
    if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else if (type === "number") {
      setForm((prev) => ({ ...prev, [name]: value === "" ? null : Number(value) }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCategoryChange = (newCategory: ReturnHomeCategory) => {
    if (newCategory !== "none" && fieldErrors.category)
      setFieldErrors((prev) => ({ ...prev, category: false }));
    setCategory(newCategory);
    const isResignation = newCategory === "resignation" || newCategory === "resignation_lumpsum";
    const return_type =
      newCategory === "paid_leave" ? 0 :
      newCategory === "resignation" ? 1 :
      newCategory === "resignation_lumpsum" ? 2 :
      newCategory === "no_paid_leave" ? 3 :
      newCategory === "finished_contract" ? 4 : -1;
    setForm((prev) => ({
      ...prev,
      return_type,
      ...(newCategory === "finished_contract" && {
        return_date: "",
      }),
      ...(!isResignation && { resign_date: null }),
      ...(newCategory !== "paid_leave" && { leave_days: null }),
    }));
  };

  const handleTranslate = async (targetLang: LanguageCode) => {
    setLangPickerOpen(false);
    setIsTranslating(true);
    try {
      const [detailsResult, commentResult] = await Promise.all([
        form.details ? translateText(form.details, targetLang) : Promise.resolve(null),
        record?.approver_remarks ? translateText(record.approver_remarks, targetLang) : Promise.resolve(null),
      ]);
      if (detailsResult?.success) setTranslatedDetails(detailsResult.translatedText);
      if (commentResult?.success) setTranslatedApproverComment(commentResult.translatedText);
      setActiveLang(targetLang);
    } catch (err) {
      console.error("Translation error:", err);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleShowOriginal = () => {
    setTranslatedDetails(null);
    setTranslatedApproverComment(null);
    setActiveLang(null);
  };

  const handleSave = async (submitAs?: "draft" | "pending") => {
    setError("");

    const newFieldErrors: Record<string, boolean> = {};
    const requiresTravelDetails = category !== "finished_contract";
    const requiresPaymentDetails = category !== "finished_contract";

    if (submitAs === "pending") {
      if (requiresTravelDetails && !form.flight_date) newFieldErrors.flight_date = true;
      if (requiresTravelDetails && !form.return_date) newFieldErrors.return_date = true;
      if (requiresTravelDetails && !form.route_origin) newFieldErrors.route_origin = true;
      if (requiresTravelDetails && !form.route_destination) newFieldErrors.route_destination = true;
    }

    if (category === "none") newFieldErrors.category = true;
    if ((category === "resignation" || category === "resignation_lumpsum") && !form.resign_date)
      newFieldErrors.resign_date = true;
    if (category === "paid_leave" && (form.leave_days == null || form.leave_days === 0))
      newFieldErrors.leave_days = true;
    if (requiresPaymentDetails && isOfficer && (form.mode_of_payment === "company" || form.mode_of_payment === "svo_advanced") &&
      (form.payment_amount == null || Number(form.payment_amount) < 0))
      newFieldErrors.payment_amount = true;

    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      setError(t("return_home.form.error_submit_required_fields"));
      return;
    }

    if (form.flight_date && form.return_date) {
      const flight = new Date(form.flight_date);
      const ret = new Date(form.return_date);
      flight.setHours(0, 0, 0, 0);
      ret.setHours(0, 0, 0, 0);
      if (ret <= flight) {
        setError(t("return_home.form.error_return_before_flight"));
        setFieldErrors({ return_date: true });
        return;
      }
    }

    if (category === "paid_leave" && form.leave_days != null && form.leave_days !== 0) {
      if (form.leave_days < 1 || form.leave_days > 99) {
        setError(t("return_home.form.error_leave_days_range"));
        setFieldErrors({ leave_days: true });
        return;
      }
    }

    if (!record) return;
    setSaving(true);
    try {
      const dataToSave = {
        ...form,
        leave_days: form.leave_days != null ? Number(form.leave_days) : null,
        payment_amount: form.payment_amount != null ? Number(form.payment_amount) : null,
      };

      if (category === "finished_contract") {
        dataToSave.return_type = 4;
        dataToSave.return_date = "";
        dataToSave.resign_date = null;
        dataToSave.leave_days = null;
      }
      if (submitAs === "pending") {
        dataToSave.status = "Pending";
      } else if (!form.status || form.status === "") {
        dataToSave.status = "Draft";
      }

      await returnHomeService.update(record.id, dataToSave);

      if (submitAs === "pending") {
        const refreshed = await returnHomeService.getById(record.id);
        if (refreshed) {
          setRecord(refreshed);
          populateForm(refreshed);
        }
      } else {
        setRecord((prev) => prev ? { ...prev, ...dataToSave } : prev);
      }

      const toastMsg = submitAs === "pending"
        ? t("return_home.form.submitted")
        : t("return_home.form.saved");
      showToast(toastMsg, "success");
      onRefresh();
    } catch (err: any) {
      const errMsg = err?.response?.data?.error || t("feed.form.save_error");
      setError(errMsg);
      showToast(errMsg, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!record?.id) return;
    if (!window.confirm(t("common.alert.delete_confirm"))) return;
    try {
      await returnHomeService.delete(record.id);
      onRefresh();
      onClose();
    } catch (err: any) {
      const errMsg = err?.response?.data?.error || t("common.error");
      showToast(errMsg, "error");
    }
  };

  const handleCancelSubmission = async () => {
    if (!record?.id) return;
    if (!window.confirm(t("return_home.form.cancel_submission_confirm"))) return;
    try {
      await returnHomeService.patchStatus(record.id, "Draft");
      showToast(t("return_home.form.submission_cancelled"), "success");
      const data = await returnHomeService.getById(record.id);
      if (data) {
        setRecord(data);
        populateForm(data);
      }
      onRefresh();
    } catch (err: any) {
      const errMsg = err?.response?.data?.error || t("common.error");
      showToast(errMsg, "error");
    }
  };

  const handleApproval = async (newStatus: "Approved" | "Rejected") => {
    if (!record?.id) return;
    const confirmMessage = newStatus === "Approved"
      ? t("return_home.form.approve_confirm_desc")
      : t("return_home.form.reject_confirm_desc");
    if (!window.confirm(confirmMessage)) return;

    setApproving(true);
    try {
      await returnHomeService.approve(record.id, { status: newStatus, approver_remarks: approverRemarks });
      showToast(t("return_home.form.approval_success", { status: newStatus }), "success");
      const data = await returnHomeService.getById(record.id);
      if (data) {
        setRecord(data);
        populateForm(data);
      }
      onRefresh();
    } catch (err: unknown) {
      const apiError = err as {
        response?: { data?: { error?: string; message?: string } };
      };
      const errMsg = apiError.response?.data?.error || apiError.response?.data?.message || t("common.error");
      showToast(errMsg, "error");
    } finally {
      setApproving(false);
    }
  };

  const handleCancelApproved = async () => {
    if (!record?.id) return;
    const reason = window.prompt(t("return_home.form.cancel_approved_prompt"));
    if (reason === null) return;
    if (!reason.trim()) {
      showToast(t("return_home.form.cancellation_reason_required"), "error");
      return;
    }

    setApproving(true);
    try {
      await returnHomeService.cancelApproved(record.id, reason.trim());
      showToast(t("return_home.form.approved_cancelled"), "success");
      const data = await returnHomeService.getById(record.id);
      if (data) {
        setRecord(data);
        populateForm(data);
      }
      onRefresh();
    } catch (err: any) {
      const errMsg = err?.response?.data?.error || err?.response?.data?.message || t("common.error");
      showToast(errMsg, "error");
    } finally {
      setApproving(false);
    }
  };

  // Attachment handlers
  const handleFilesSelected = (files: File[]) => {
    if (!record?.id) return;
    for (const file of files) {
      const mimeType = getMimeType(file.name);
      if (!ACCEPTED_MIME_TYPES.includes(mimeType)) { setError(t("feed.form.invalid_file_type")); continue; }
      if (file.size > MAX_FILE_SIZE) { setError(t("feed.form.file_too_large")); continue; }

      const localId = typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : Date.now().toString() + Math.random().toString(36);

      setUploads((prev) => [...prev, { localId, file, status: "uploading", progress: 0 }]);

      (async () => {
        try {
          const { uploadUrl, s3Key, bucketName } = await getPresignedUrl(file.name, mimeType, "return_home", record.id);
          await uploadToS3Direct(uploadUrl, file, mimeType, (progress) => {
            setUploads((prev) => prev.map((u) => u.localId === localId ? { ...u, progress } : u));
          });
          const confirmed = await confirmAttachmentInDB({
            relation_type: "return_home",
            relation_id: record.id,
            s3_key: s3Key,
            s3_bucket: bucketName,
            display_name: file.name,
            file_type: mimeType,
            file_size: file.size,
          });
          setUploads((prev) => prev.map((u) => u.localId === localId ? { ...u, status: "done", progress: 100, attachment: confirmed } : u));
          setAttachments((prev) => [...prev, confirmed]);
          setTimeout(() => setUploads((prev) => prev.filter((u) => u.localId !== localId)), 1000);
        } catch (err: any) {
          setUploads((prev) => prev.map((u) => u.localId === localId ? { ...u, status: "error", error: err.message } : u));
        }
      })();
    }
  };

  const handleAttachmentDelete = async (attachmentId: number | string) => {
    try {
      await deleteAttachmentFromAll(attachmentId);
      setAttachments((prev) => prev.filter((a) => a.attachment_id !== attachmentId));
    } catch (err) {
      console.error("Failed to delete attachment:", err);
    }
  };

  const handleAttachmentRename = async (attachmentId: number | string, newName: string) => {
    try {
      await announcementService.renameAttachment(attachmentId, newName);
      setAttachments((prev) => prev.map((a) => a.attachment_id === attachmentId ? { ...a, display_name: newName } : a));
    } catch (err) {
      console.error("Failed to rename attachment:", err);
    }
  };

  const handleCancelUpload = (localId: string) => {
    setUploads((prev) => prev.filter((u) => u.localId !== localId));
  };

  // Derived state
  const travelStatus = computeStatus(form.flight_date, form.return_date);
  const isTranslated = !!(translatedDetails || translatedApproverComment);
  const isApprovedOrRejected = record && ["Approved", "Rejected", "Cancelled"].includes(record.status || "");
  const isPending = !!(record && record.status === "Pending");
  const officerApproved = isOfficer && !!(record && record.status === "Approved");
  const fieldDisabled = (!officerApproved && (!!isApprovedOrRejected || isPending));
  const officerApprovedOrPending = isOfficer && !!(record && ["Approved", "Pending"].includes(record.status || ""));
  const paymentTioDisabled = officerApprovedOrPending ? false : fieldDisabled;

  const tabs: { id: TabId; label: string; count?: number }[] = [
    { id: "info", label: t("tasks.detail.info") },
    { id: "comments", label: t("tasks.detail.comments"), count: commentCount || initialCommentCount },
    { id: "files", label: t("tasks.detail.files"), count: attachments.length },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Toast */}
        {toast && (
          <div className="absolute top-4 left-4 right-4 z-10">
            <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
          </div>
        )}

        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-gray-200 gap-3 shrink-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              {record && (
                <span className="text-xs font-medium text-gray-400">#{record.id}</span>
              )}
              {record?.status && (
                <Badge variant={APPROVAL_VARIANT[record.status] || "default"}>
                  {t(`return_home.approval.status_${record.status.toLowerCase()}`)}
                </Badge>
              )}
              {travelStatus && record?.status !== "Rejected" && (
                <Badge variant={statusVariant[travelStatus] || "default"}>
                  {t(STATUS_I18N_MAP[travelStatus] || travelStatus)}
                </Badge>
              )}
            </div>
            <h2 className="text-base font-semibold text-gray-900 line-clamp-2 leading-snug">
              {record?.user_name || t("return_home.title")}
            </h2>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {/* Translate button */}
            <div className="relative" ref={langPickerRef}>
              <button
                type="button"
                onClick={() => {
                  if (isTranslated) handleShowOriginal();
                  else setLangPickerOpen((prev) => !prev);
                }}
                disabled={isTranslating}
                title={isTranslated ? t("feed.show_original") : t("feed.translate")}
                className={`inline-flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 ${
                  isTranslated
                    ? "bg-indigo-100 text-indigo-700"
                    : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                }`}
              >
                {isTranslating ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                )}
                {activeLang && !isTranslating && (
                  <span className="font-semibold">{activeLang.toUpperCase()}</span>
                )}
              </button>
              {langPickerOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 min-w-[180px] py-1 overflow-y-auto max-h-[50vh]">
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => handleTranslate(lang.code as LanguageCode)}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <span>{LANGUAGE_FLAGS[lang.code] || ""}</span>
                      <span>{LANGUAGE_NATIVE_NAMES[lang.code] || lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Save / Submit buttons */}
            {record?.status === "Draft" && (
              <>
                <Button
                  size="sm"
                  loading={saving}
                  disabled={fieldDisabled}
                  onClick={() => handleSave("pending")}
                  className="!bg-blue-600 hover:!bg-blue-700 !text-white text-xs px-2"
                >
                  {t("return_home.form.submit_for_approval")}
                </Button>
                <Button size="sm" loading={saving} disabled={fieldDisabled} onClick={() => handleSave()}>
                  {t("return_home.form.save")}
                </Button>
              </>
            )}
            {record?.status === "Pending" && (
              <Button size="sm" loading={saving} disabled={fieldDisabled} onClick={() => handleSave()}>
                {t("return_home.form.update")}
              </Button>
            )}
            {record && !["Draft", "Pending"].includes(record.status || "") && (
              <Button
                size="sm"
                loading={saving}
                disabled={officerApprovedOrPending ? saving : fieldDisabled}
                onClick={() => handleSave()}
              >
                {t("return_home.form.update")}
              </Button>
            )}

            {/* Close */}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 shrink-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? "text-indigo-600 border-b-2 border-indigo-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <span className="flex items-center justify-center gap-1.5">
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && (
                  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full leading-none ${
                    activeTab === tab.id ? "bg-indigo-100 text-indigo-600" : "bg-gray-100 text-gray-500"
                  }`}>
                    {tab.count}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Spinner />
            </div>
          ) : !record ? null : (
            <>
              {/* INFO TAB */}
              {activeTab === "info" && (
                <div className="p-4 space-y-4">
                  {/* Lumpsum alert */}
                  {category === "resignation_lumpsum" && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-800 border border-red-200 rounded-lg text-sm font-medium">
                      <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      {t("return_home.form.lumpsum_alert")}
                    </div>
                  )}

                  {/* Trip summary */}
                  <TripSummaryCard
                    origin={form.route_origin}
                    destination={form.route_destination}
                    flightDate={form.flight_date}
                    returnDate={form.return_date}
                    oneWay={category === "finished_contract"}
                  />

                  {/* User profile info */}
                  <div className="grid grid-cols-2 gap-2 p-3 bg-gray-50 rounded-lg border border-gray-100 text-xs">
                    <div>
                      <p className="text-gray-500 font-medium mb-0.5">{t("return_home.info.name")}</p>
                      <p className="text-gray-800">{record.user_name || "-"}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 font-medium mb-0.5">{t("return_home.info.company")}</p>
                      <p className="text-gray-800">{record.company_name_text || "-"}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 font-medium mb-0.5">{t("return_home.info.visa_type")}</p>
                      <p className="text-gray-800">{record.visa_type_descr || record.visa_type || "-"}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 font-medium mb-0.5">{t("return_home.info.joining_date")}</p>
                      <p className="text-gray-800">{record.joining_date ? fmtDate(record.joining_date) : "-"}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 font-medium mb-0.5">{t("return_home.info.visa_expiry")}</p>
                      <p className="text-gray-800">{record.visa_expiry_date ? fmtDate(record.visa_expiry_date) : "-"}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 font-medium mb-0.5">{t("profile.country")}</p>
                      <p className="text-gray-800">
                        {record.country ? t(`profile.country_${record.country.toLowerCase()}`, { defaultValue: record.country }) : "-"}
                      </p>
                    </div>
                  </div>

                  {fieldDisabled ? (
                    /* Locked: show values as text instead of disabled inputs */
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3 p-3 rounded-lg border border-gray-100">
                      <ReadOnlyField
                        id="field-category"
                        className="col-span-2"
                        label={t("return_home.form.category")}
                        value={CATEGORY_LABEL_KEY[category] ? t(CATEGORY_LABEL_KEY[category] as string) : null}
                      />
                      {(category === "resignation" || category === "resignation_lumpsum") && (
                        <ReadOnlyField
                          id="field-resign_date"
                          label={t("return_home.form.resign_date")}
                          value={form.resign_date ? fmtDate(form.resign_date) : null}
                        />
                      )}
                      {category === "paid_leave" && (
                        <ReadOnlyField
                          id="field-leave_days"
                          label={t("return_home.form.leave_days")}
                          value={form.leave_days != null ? String(form.leave_days) : null}
                        />
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Flight / Return Date */}
                      <div className="grid grid-cols-2 gap-3">
                        <div id="field-flight_date">
                          <Input
                            label={t("return_home.form.flight_date")}
                            name="flight_date"
                            type="date"
                            value={form.flight_date}
                            onChange={handleChange}
                            disabled={fieldDisabled}
                            error={fieldErrors.flight_date ? " " : undefined}
                          />
                        </div>
                        {category !== "finished_contract" && (
                          <div id="field-return_date">
                            <Input
                              label={t("return_home.form.return_date")}
                              name="return_date"
                              type="date"
                              value={form.return_date}
                              onChange={handleChange}
                              disabled={fieldDisabled}
                              min={
                                form.flight_date
                                  ? new Date(new Date(form.flight_date).getTime() + 86400000).toISOString().slice(0, 10)
                                  : new Date(Date.now() + 86400000).toISOString().slice(0, 10)
                              }
                              error={fieldErrors.return_date ? " " : undefined}
                            />
                          </div>
                        )}
                      </div>

                      {/* Route Origin */}
                      <div className="grid grid-cols-5 gap-3">
                        <div className="col-span-2">
                          <div id="field-route_origin" className="space-y-1">
                            <label className={`block text-sm font-medium ${fieldDisabled ? "text-gray-400" : fieldErrors.route_origin ? "text-red-600" : "text-gray-700"}`}>
                              {t("return_home.form.origin_country")}
                            </label>
                            <select
                              value={originCountry}
                              onChange={(e) => { setOriginCountry(e.target.value); if (fieldErrors.route_origin) setFieldErrors((prev) => ({ ...prev, route_origin: false })); }}
                              disabled={fieldDisabled}
                              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${fieldDisabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""} ${fieldErrors.route_origin ? "border-red-300 ring-2 ring-red-100" : "border-gray-300"}`}
                            >
                              <option value="">{t("return_home.form.select_country")}</option>
                              {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                        </div>
                        <div className="col-span-3">
                          {originCountry && (
                            <div className="space-y-1">
                              <label className={`block text-sm font-medium ${fieldDisabled ? "text-gray-400" : "text-gray-700"}`}>
                                {t("return_home.form.origin_airport")}
                              </label>
                              <select
                                name="route_origin"
                                value={form.route_origin}
                                onChange={handleChange}
                                disabled={fieldDisabled}
                                className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${fieldDisabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""}`}
                              >
                                <option value="">{t("return_home.form.select_airport")}</option>
                                {originAirports.map((a) => (
                                  <option key={a.code} value={a.code}>{a.code} - {a.city} ({a.name})</option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Route Destination */}
                      <div className="grid grid-cols-5 gap-3">
                        <div className="col-span-2">
                          <div id="field-route_destination" className="space-y-1">
                            <label className={`block text-sm font-medium ${fieldDisabled ? "text-gray-400" : fieldErrors.route_destination ? "text-red-600" : "text-gray-700"}`}>
                              {t("return_home.form.dest_country")}
                            </label>
                            <select
                              value={destCountry}
                              onChange={(e) => { setDestCountry(e.target.value); if (fieldErrors.route_destination) setFieldErrors((prev) => ({ ...prev, route_destination: false })); }}
                              disabled={fieldDisabled}
                              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${fieldDisabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""} ${fieldErrors.route_destination ? "border-red-300 ring-2 ring-red-100" : "border-gray-300"}`}
                            >
                              <option value="">{t("return_home.form.select_country")}</option>
                              {COUNTRIES.filter((c) => c !== originCountry).map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                        </div>
                        <div className="col-span-3">
                          {destCountry && (
                            <div className="space-y-1">
                              <label className={`block text-sm font-medium ${fieldDisabled ? "text-gray-400" : "text-gray-700"}`}>
                                {t("return_home.form.dest_airport")}
                              </label>
                              <select
                                name="route_destination"
                                value={form.route_destination}
                                onChange={handleChange}
                                disabled={fieldDisabled}
                                className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${fieldDisabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""}`}
                              >
                                <option value="">{t("return_home.form.select_airport")}</option>
                                {destAirports.map((a) => (
                                  <option key={a.code} value={a.code}>{a.code} - {a.city} ({a.name})</option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Category */}
                      <div id="field-category" className="space-y-1">
                        <label className={`block text-sm font-medium ${fieldDisabled ? "text-gray-400" : fieldErrors.category ? "text-red-600" : "text-gray-700"}`}>
                          {t("return_home.form.category")}
                        </label>
                        <select
                          value={category}
                          onChange={(e) => handleCategoryChange(e.target.value as any)}
                          disabled={fieldDisabled}
                          className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${fieldDisabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""} ${fieldErrors.category ? "border-red-300 ring-2 ring-red-100" : "border-gray-300"}`}
                        >
                          <option value="none">{t("return_home.form.category_none")}</option>
                          <option value="paid_leave">{t("return_home.form.category_paid_leave")}</option>
                          <option value="no_paid_leave">{t("return_home.form.category_no_paid_leave")}</option>
                          <option value="resignation">{t("return_home.form.category_resignation")}</option>
                          <option value="resignation_lumpsum">{t("return_home.form.category_resignation_two")}</option>
                          <option value="finished_contract">{t("return_home.form.category_finished_contract")}</option>
                        </select>
                        {category === "paid_leave" && (
                          <p className="text-xs text-gray-500 mt-1">{t("return_home.form.leave_note_one")}</p>
                        )}
                        {(category === "resignation" || category === "resignation_lumpsum") && (
                          <p className="text-xs text-gray-500 mt-1">{t("return_home.form.resignation_note_one")}</p>
                        )}
                      </div>

                      {/* Resignation sub-fields */}
                      {(category === "resignation" || category === "resignation_lumpsum") && (
                        <div className="space-y-3 pl-3 border-l-2 border-orange-200">
                          <div id="field-resign_date">
                            <Input
                              label={t("return_home.form.resign_date")}
                              name="resign_date"
                              type="date"
                              value={form.resign_date || ""}
                              onChange={handleChange}
                              disabled={fieldDisabled}
                              error={fieldErrors.resign_date ? " " : undefined}
                            />
                          </div>
                          <p className="text-xs text-gray-500">{t("return_home.form.resignation_note_two")}</p>
                        </div>
                      )}

                      {/* Paid leave sub-fields */}
                      {category === "paid_leave" && (
                        <div className="pl-3 border-l-2 border-blue-200">
                          <div id="field-leave_days">
                            <Input
                              label={t("return_home.form.leave_days")}
                              name="leave_days"
                              type="number"
                              value={form.leave_days != null ? String(form.leave_days) : ""}
                              onChange={handleChange}
                              disabled={fieldDisabled}
                              min="1"
                              max="99"
                              error={fieldErrors.leave_days ? " " : undefined}
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Payment Details */}
                  <div className="space-y-3 pt-4 border-t border-gray-100">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {t("return_home.form.payment_section")}
                    </p>
                    {fieldDisabled ? (
                      <ReadOnlyField
                        label={t("return_home.form.mode_of_payment")}
                        value={
                          form.mode_of_payment === "company" ? t("return_home.form.payment_company") :
                          form.mode_of_payment === "self" ? t("return_home.form.payment_self") :
                          form.mode_of_payment === "svo_advanced" ? t("return_home.form.payment_svo_advanced", { defaultValue: "Request ticket to SVO, to be billed to the company." }) :
                          null
                        }
                      />
                    ) : (
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">
                        {t("return_home.form.mode_of_payment")}
                      </label>
                      <select
                        name="mode_of_payment"
                        value={form.mode_of_payment || ""}
                        onChange={(e) => setForm((prev) => ({
                          ...prev,
                          mode_of_payment: (e.target.value as "company" | "self" | "svo_advanced") || null,
                          ...(e.target.value !== "company" && e.target.value !== "svo_advanced" && { payment_amount: null }),
                        }))}
                        disabled={fieldDisabled}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${fieldDisabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""}`}
                      >
                        <option value="">—</option>
                        <option value="company">{t("return_home.form.payment_company")}</option>
                        <option value="self">{t("return_home.form.payment_self")}</option>
                        <option value="svo_advanced">{t("return_home.form.payment_svo_advanced", { defaultValue: "Request ticket to SVO, to be billed to the company." })}</option>
                      </select>
                      {form.mode_of_payment === "svo_advanced" && (
                        <p className="text-xs text-gray-500 mt-1">{t("return_home.form.svo_advanced_note", { defaultValue: "Note: The applicant will pay directly to the company." })}</p>
                      )}
                    </div>
                    )}
                    {(form.mode_of_payment === "company" || form.mode_of_payment === "svo_advanced") && isOfficer && (
                      <>
                        <div id="field-payment_amount" className="flex gap-2 items-end">
                          <div className="flex-1">
                            <Input
                              label={t("return_home.form.payment_amount")}
                              name="payment_amount"
                              type="number"
                              value={form.payment_amount != null ? String(form.payment_amount) : ""}
                              onChange={handleChange}
                              disabled={paymentTioDisabled}
                              min="0"
                              error={fieldErrors.payment_amount ? " " : undefined}
                            />
                          </div>
                          <div className="space-y-1 shrink-0">
                            <label className={`block text-sm font-medium ${fieldDisabled ? "text-gray-400" : "text-gray-700"}`}>
                              {t("return_home.form.currency")}
                            </label>
                            <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm text-gray-600 font-medium">
                              {form.currency || "JPY"}
                            </div>
                          </div>
                        </div>

                        {form.payment_amount != null && Number(form.payment_amount) > 0 && (
                          <div className={`mt-3 rounded-lg border-2 px-4 py-3 flex items-center justify-between gap-4 ${form.payment_settled ? "bg-green-50 border-green-400" : "bg-amber-50 border-amber-400"}`}>
                            <div className="flex items-center gap-2">
                              <span className={`text-xl ${form.payment_settled ? "text-green-500" : "text-amber-500"}`}>
                                {form.payment_settled ? "✅" : "⏳"}
                              </span>
                              <div>
                                <p className={`text-sm font-semibold ${form.payment_settled ? "text-green-800" : "text-amber-800"}`}>
                                  {t("return_home.form.payment_settled")}
                                </p>
                                <p className={`text-xs ${form.payment_settled ? "text-green-600" : "text-amber-600"}`}>
                                  {form.payment_settled ? t("return_home.form.payment_settled_yes") : t("return_home.form.payment_settled_no")}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <button
                                type="button"
                                disabled={paymentTioDisabled}
                                onClick={() => setForm((prev) => ({ ...prev, payment_settled: false }))}
                                className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${!form.payment_settled ? "bg-amber-500 text-white border-amber-500" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"} ${paymentTioDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                              >
                                {t("return_home.form.payment_settled_no_btn")}
                              </button>
                              <button
                                type="button"
                                disabled={paymentTioDisabled}
                                onClick={() => setForm((prev) => ({ ...prev, payment_settled: true }))}
                                className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${form.payment_settled ? "bg-green-500 text-white border-green-500" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"} ${paymentTioDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                              >
                                {t("return_home.form.payment_settled_yes_btn")}
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* TIO / JO */}
                  {isOfficer && (
                    <Input
                      label={t("return_home.form.tio_jo")}
                      name="tio_jo"
                      value={form.tio_jo}
                      onChange={handleChange}
                      disabled={paymentTioDisabled}
                    />
                  )}

                  {/* Details */}
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      {t("return_home.form.details")}
                    </label>
                    {translatedDetails ? (
                      <>
                        <p className="px-3 py-2 text-sm text-gray-800 bg-indigo-50 border border-indigo-100 rounded-lg whitespace-pre-wrap">
                          {translatedDetails}
                        </p>
                        <p className="text-[10px] text-indigo-400 italic">{t("feed.translated")}</p>
                      </>
                    ) : fieldDisabled ? (
                      <p className="px-3 py-2 text-sm text-gray-800 bg-gray-50 border border-gray-100 rounded-lg whitespace-pre-wrap">
                        {form.details || "—"}
                      </p>
                    ) : (
                      <textarea
                        name="details"
                        value={form.details}
                        onChange={handleChange}
                        disabled={fieldDisabled}
                        rows={4}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${fieldDisabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""}`}
                      />
                    )}
                  </div>

                  {/* Approver Comment */}
                  {record.approver_remarks && (
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 text-sm">
                      <span className="text-gray-500">{t("return_home.form.approver_comment")}:</span>
                      {translatedApproverComment ? (
                        <>
                          <p className="text-gray-800 mt-1 whitespace-pre-wrap">{translatedApproverComment}</p>
                          <p className="text-[10px] text-indigo-400 italic mt-1">{t("feed.translated")}</p>
                        </>
                      ) : (
                        <p className="text-gray-800 mt-1">{record.approver_remarks}</p>
                      )}
                    </div>
                  )}

                  {/* Officer Approval Workflow */}
                  {canApprove && record.status && ["Pending", "Approved", "Rejected", "Cancelled"].includes(record.status) && (
                    <div className="space-y-3 pt-4 border-t border-gray-200">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        {t("return_home.form.approval_section")}
                      </p>
                      {record.status === "Pending" && (
                        <div className="space-y-1">
                          <label className="block text-sm font-medium text-gray-700">
                            {t("return_home.form.approver_remarks")}
                          </label>
                          <textarea
                            value={approverRemarks}
                            onChange={(e) => setApproverRemarks(e.target.value)}
                            rows={2}
                            placeholder={t("return_home.form.remarks_placeholder")}
                            disabled={!!isApprovedOrRejected}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${isApprovedOrRejected ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""}`}
                          />
                        </div>
                      )}
                      {record.status === "Pending" && (
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            onClick={() => handleApproval("Approved")}
                            loading={approving}
                            disabled={(form.mode_of_payment === "company" || form.mode_of_payment === "svo_advanced") && (form.payment_amount == null || Number(form.payment_amount) < 0)}
                            className="flex-1 !bg-green-600 hover:!bg-green-700"
                          >
                            {t("return_home.form.approve")}
                          </Button>
                          <Button
                            type="button"
                            variant="danger"
                            onClick={() => handleApproval("Rejected")}
                            loading={approving}
                            className="flex-1"
                          >
                            {t("return_home.form.reject")}
                          </Button>
                        </div>
                      )}
                      {record.status === "Approved" && (
                        <Button
                          type="button"
                          variant="danger"
                          onClick={handleCancelApproved}
                          loading={approving}
                          className="w-full"
                        >
                          {t("return_home.form.cancel_approved")}
                        </Button>
                      )}
                    </div>
                  )}

                  {record.status === "Cancelled" && (
                    <div className="p-3 bg-red-50 rounded-lg border border-red-100 text-sm">
                      <p className="font-medium text-red-800">{t("return_home.form.cancelled_label")}</p>
                      <p className="mt-1 text-red-700 whitespace-pre-wrap">{record.cancellation_reason}</p>
                      {(record.cancelled_by_name || record.cancelled_by || record.cancelled_at) && (
                        <p className="mt-1 text-xs text-red-600">
                          {record.cancelled_by_name || record.cancelled_by || "-"}
                          {record.cancelled_at ? ` · ${fmtDate(record.cancelled_at)}` : ""}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Audit fields */}
                  <div className="pt-4 border-t border-gray-200 space-y-2">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                      {t("return_home.audit")}
                    </p>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500">{t("return_home.info.application_date")}:</span>
                        <p className="text-gray-800">{record.application_date ? fmtDate(record.application_date) : "-"}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">{t("return_home.info.created")}:</span>
                        <p className="text-gray-800">{record.created_at ? fmtDate(record.created_at) : "-"}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">{t("return_home.info.last_updated")}:</span>
                        <p className="text-gray-800">{record.updated_at ? fmtDate(record.updated_at) : "-"}</p>
                      </div>
                      {record.approved_by && (
                        <div>
                          <span className="text-gray-500">{t("return_home.info.approved_at")}:</span>
                          <p className="text-gray-800">{record.approved_at ? fmtDate(record.approved_at) : "-"}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {error && <p className="text-sm text-red-600">{error}</p>}

                  {/* Delete button */}
                  {record.status === "Draft" && (
                    <div className="flex pt-2">
                      <Button variant="danger" type="button" onClick={handleDelete} className="w-full sm:w-auto">
                        {t("common.delete")}
                      </Button>
                    </div>
                  )}

                  {/* Cancel Submission button */}
                  {record.status === "Pending" && (
                    <div className="flex pt-2">
                      <Button variant="danger" type="button" onClick={handleCancelSubmission} className="w-full sm:w-auto">
                        {t("return_home.form.cancel_submission")}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* COMMENTS TAB */}
              {activeTab === "comments" && (
                <div className="p-4">
                  <CommentSection
                    announcementId={record.id}
                    relationType="return_home"
                    currentUserId={userData?.id ?? ""}
                    currentUserName={formatDisplayName(userData?.lastName, userData?.firstName, userData?.middleName)}
                    isOfficer={isOfficer}
                    onCountChange={setCommentCount}
                  />
                </div>
              )}

              {/* FILES TAB */}
              {activeTab === "files" && (
                <div className="p-4 space-y-3">
                  <AttachmentDropZone
                    onFilesSelected={handleFilesSelected}
                    accept=".pdf,.jpg,.jpeg,.png,.gif"
                    disabled={uploads.some((u) => u.status === "uploading")}
                  />
                  <AttachmentList
                    attachments={attachments}
                    uploads={uploads}
                    onDelete={handleAttachmentDelete}
                    onRename={handleAttachmentRename}
                    onCancelUpload={handleCancelUpload}
                  />
                  {attachments.length === 0 && uploads.length === 0 && (
                    <p className="text-xs text-gray-400 italic text-center py-2">
                      {t("return_home.sidebar.no_attachments")}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};
